import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { POPOVER_Z_INDEX } from './z-index'

const VIEWPORT_MARGIN = 8

export type TooltipPlacement =
    | 'top'
    | 'topStart'
    | 'topEnd'
    | 'right'
    | 'rightStart'
    | 'rightEnd'
    | 'bottom'
    | 'bottomStart'
    | 'bottomEnd'
    | 'left'
    | 'leftStart'
    | 'leftEnd'

export interface TooltipProps {
    content: React.ReactNode
    children: React.ReactElement
    placement?: TooltipPlacement
    inverse?: boolean
    /** Optional accent fill for feature-discovery hints. */
    tone?: 'neutral' | 'accent'
    showArrow?: boolean
    delay?: number
    style?: React.CSSProperties
    /**
     * Pins the bubble open regardless of hover, for a message the surface has to keep showing on its
     * own — a bulk update marking the one row that failed, say. Undefined by default, so hover stays
     * the only thing that opens it for every existing caller.
     */
    open?: boolean
    /**
     * Extra classes for the wrapper this puts around `children`.
     *
     * The wrapper already carries `min-w-0 max-w-full`, so a truncating child truncates without the
     * caller asking; this is for anything beyond that — a width, a grow, an alignment the trigger
     * needs in its own row. Empty by default, so every existing caller renders exactly as before.
     */
    wrapperClassName?: string
}

// Keeps the bubble inside the viewport with an 8px margin. Without this a bubble anchored near an
// edge — a tooltip on the last table column, or any long content string — renders partly off-screen
// and is unreadable, since the bubble is portaled to <body> at position:fixed.
const clampToViewport = (top: number, left: number, bubbleSize: { width: number; height: number }) => ({
    top: Math.max(VIEWPORT_MARGIN, Math.min(top, window.innerHeight - bubbleSize.height - VIEWPORT_MARGIN)),
    left: Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - bubbleSize.width - VIEWPORT_MARGIN)),
})

const getFixedPosition = (triggerRect: DOMRect, bubbleSize: { width: number; height: number }, placement: TooltipPlacement, gap: number) => {
    const side = placement.replace(/Start|End/i, '').toLowerCase()
    const isStart = placement.includes('Start')
    const isEnd = placement.includes('End')

    if (side === 'top' || side === 'bottom') {
        const left = isStart ? triggerRect.left : isEnd ? triggerRect.right - bubbleSize.width : triggerRect.left + triggerRect.width / 2 - bubbleSize.width / 2
        const top = side === 'top' ? triggerRect.top - gap - bubbleSize.height : triggerRect.bottom + gap
        return clampToViewport(top, left, bubbleSize)
    }

    const top = isStart ? triggerRect.top : isEnd ? triggerRect.bottom - bubbleSize.height : triggerRect.top + triggerRect.height / 2 - bubbleSize.height / 2
    const left = side === 'left' ? triggerRect.left - gap - bubbleSize.width : triggerRect.right + gap
    return clampToViewport(top, left, bubbleSize)
}

// The DS tooltip is a pill, which only holds while its content fits on one line: `rounded-full`
// resolves to half the bubble's height, so on a wrapped bubble the curve cuts straight through the
// first and last line of text and the content reads as floating outside its own container (the long
// help strings on the truck form's Permits & Restrictions step). Wrapped bubbles get the overlay
// radius instead, and room to breathe around a paragraph.
const isWrapped = (pill: HTMLElement) => {
    const styles = window.getComputedStyle(pill)
    const lineHeight = parseFloat(styles.lineHeight)
    // jsdom (and `line-height: normal`) report no resolvable line height — stay a pill rather than guess.
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) return false
    const textHeight = pill.clientHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom)
    return textHeight > lineHeight + 1
}

const getArrowStyle = (placement: TooltipPlacement, size: number): React.CSSProperties => {
    const side = placement.replace(/Start|End/i, '').toLowerCase()
    const isStart = placement.includes('Start')
    const isEnd = placement.includes('End')

    const base: React.CSSProperties = { position: 'absolute', width: size, height: size, borderRadius: 2, transform: 'rotate(45deg)' }

    const hPos = isStart ? { left: 12 } : isEnd ? { right: 12 } : { left: '50%', marginLeft: -size / 2 }
    const vPos = isStart ? { top: 12 } : isEnd ? { bottom: 12 } : { top: '50%', marginTop: -size / 2 }

    if (side === 'top') return { ...base, bottom: -size / 2, ...hPos }
    if (side === 'bottom') return { ...base, top: -size / 2, ...hPos }
    if (side === 'left') return { ...base, right: -size / 2, ...vPos }
    if (side === 'right') return { ...base, left: -size / 2, ...vPos }
    return base
}

export const Tooltip = ({
    content,
    children,
    placement = 'top',
    inverse = false,
    tone = 'neutral',
    showArrow = true,
    delay = 300,
    open,
    wrapperClassName = '',
}: TooltipProps) => {
    const [hovered, setHovered] = useState(false)
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
    const [wrapped, setWrapped] = useState(false)
    const timerRef = useRef<ReturnType<typeof setTimeout>>()
    const triggerRef = useRef<HTMLDivElement>(null)
    const bubbleRef = useRef<HTMLDivElement>(null)
    const pillRef = useRef<HTMLDivElement>(null)
    const describedById = React.useId()

    const bgClass = tone === 'accent' ? 'bg-accent' : inverse ? 'bg-background-inverse' : 'bg-surface'
    const colorClass = tone === 'accent' ? 'text-accent-foreground' : inverse ? 'text-background' : 'text-foreground'

    // A pinned bubble is shown by its caller, not found by the user, so it skips the hover delay.
    const visible = Boolean(open || hovered)

    // How long the text is decides how wide the bubble is, so a PINNED bubble whose message changes
    // under it has to be re-measured — nothing else would re-open it. Only plain text is watched:
    // rich content is a fresh element on every parent render, and keying off its identity would
    // re-measure on every one of them for no gain.
    const textContent = typeof content === 'string' || typeof content === 'number' ? content : null

    const show = useCallback(() => {
        timerRef.current = setTimeout(() => setHovered(true), delay)
    }, [delay])

    const hide = useCallback(() => {
        clearTimeout(timerRef.current)
        setHovered(false)
    }, [])

    useEffect(() => () => clearTimeout(timerRef.current), [])

    // Runs before paint: measures the bubble's real size against the trigger's position so it can
    // be portaled to <body> with pixel-accurate placement instead of being CSS-relative to the
    // trigger — a relative bubble gets silently clipped whenever any ancestor between it and the
    // trigger has overflow:hidden (rounded-corner list rows, scrollable panels, etc.).
    // Wrapping is resolved here too, and `wrapped` is a dependency: a wrapped bubble carries more
    // padding, so the pass that detects it measures the pill it is about to stop being. Re-running
    // once against the final padding keeps the placement exact, and settles because the second pass
    // reads the same wrap state.
    useLayoutEffect(() => {
        // Dropping the position on the way out — rather than in the mouse-leave handler — is what
        // lets a pinned bubble survive a hover that ends while it is still open, and still keeps a
        // re-opened bubble from flashing at wherever it sat last time before it is re-measured.
        if (!visible) {
            setPos(null)
            return
        }
        if (!triggerRef.current || !bubbleRef.current || !pillRef.current) return
        const triggerRect = triggerRef.current.getBoundingClientRect()
        const bubbleSize = { width: bubbleRef.current.offsetWidth, height: bubbleRef.current.offsetHeight }
        setWrapped(isWrapped(pillRef.current))
        setPos(getFixedPosition(triggerRect, bubbleSize, placement, 8))
    }, [visible, placement, wrapped, textContent])

    // The visible bubble only exists while hovered/focused, so it cannot be the accessible
    // description on its own. A permanently rendered visually-hidden copy is what assistive tech
    // reads, and the trigger points at it via aria-describedby.
    const describedChild = React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<{ 'aria-describedby'?: string }>, { 'aria-describedby': describedById })
        : children

    // min-w-0 / max-w-full on the trigger: the wrapper is frequently a flex item sitting next to a
    // shrink-0 sibling (a driver name next to its "Incomplete" badge in a fixed-width table cell).
    // Default `min-width: auto` stops it shrinking below its content, so a truncating child never
    // truncates and the whole group overflows the cell and paints over the next column.
    return (
        <div
            ref={triggerRef}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
            className={`relative inline-flex min-w-0 max-w-full ${wrapperClassName}`}
        >
            {describedChild}
            <span id={describedById} className="sr-only">
                {content}
            </span>
            {visible &&
                createPortal(
                    <div
                        ref={bubbleRef}
                        // pointerEvents none: the bubble is purely decorative (aria-hidden) and sits at
                        // z-10000 — without it, a still-visible bubble (e.g. over a keyboard-focused
                        // trigger) would swallow clicks meant for the controls underneath it.
                        style={{
                            position: 'fixed',
                            top: pos?.top ?? -9999,
                            left: pos?.left ?? -9999,
                            zIndex: POPOVER_Z_INDEX,
                            visibility: pos ? 'visible' : 'hidden',
                            pointerEvents: 'none',
                        }}
                        aria-hidden="true"
                    >
                        <div
                            ref={pillRef}
                            role="tooltip"
                            // max-w-xs + wrapping, not whitespace-nowrap: short labels still render as a single-line
                            // pill, but long content (restriction messages, joined dispatcher lists) wraps instead of
                            // running off-screen as one unbroken line.
                            className={`relative max-w-xs break-words text-xs font-normal leading-[1.34] shadow-overlay backdrop-blur-[20px] ${
                                wrapped ? 'rounded-xl px-3 py-2' : 'rounded-full px-2 py-1'
                            } ${bgClass} ${colorClass}`}
                        >
                            {content}
                            {showArrow && <div className={bgClass} style={getArrowStyle(placement, 10)} />}
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    )
}
